/**
 * Language Picker Component
 *
 * Allows users to select their preferred language from the supported options.
 * Displays a dropdown picker with English, Simplified Chinese, and Traditional Chinese.
 * Follows the same pattern as the base currency picker in ProfileScreen.
 */

import React, { useMemo } from 'react';
import { View, Text, StyleSheet, ActivityIndicator, Platform } from 'react-native';
import { Picker } from '@react-native-picker/picker';
import { useLanguage } from '../../contexts/LanguageContext';
import { SUPPORTED_LANGUAGES, type LanguageCode } from '../../i18n/config';
import { useTheme } from '../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

interface LanguageOption {
  value: LanguageCode;
  label: string;
}

export function LanguagePicker() {
  const { language, setLanguage, isInitialized } = useLanguage();
  const { t } = useTranslation();
  const { theme } = useTheme();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const [isChanging, setIsChanging] = React.useState(false);

  const languageOptions: LanguageOption[] = [
    { value: 'en', label: SUPPORTED_LANGUAGES.en },
    { value: 'zh-CN', label: SUPPORTED_LANGUAGES['zh-CN'] },
    { value: 'zh-TW', label: SUPPORTED_LANGUAGES['zh-TW'] },
  ];

  const handleLanguageChange = async (newLanguage: LanguageCode) => {
    if (isChanging || newLanguage === language) return;

    setIsChanging(true);
    try {
      await setLanguage(newLanguage);
    } finally {
      setIsChanging(false);
    }
  };

  if (!isInitialized) {
    return (
      <View style={styles.sectionContainer}>
        <Text style={styles.sectionLabel}>{t('settings.language')}</Text>
        <ActivityIndicator size="small" color={theme.colors.primary} />
      </View>
    );
  }

  return (
    <View style={styles.sectionContainer}>
      <Text style={styles.sectionLabel}>{t('settings.language')}</Text>
      <Text style={styles.sectionDescription}>{t('settings.languageDescription')}</Text>

      <View
        style={[
          styles.pickerWrapper,
          {
            borderColor: theme.colors.border,
            borderWidth: theme.id === 'bauhaus' ? 2 : 1,
          },
        ]}
      >
        <Picker
          selectedValue={language}
          onValueChange={handleLanguageChange}
          enabled={!isChanging}
          style={[
            styles.picker,
            {
              color: theme.colors.text,
              backgroundColor: theme.colors.backgroundElevated,
            },
          ]}
          dropdownIconColor={theme.colors.textSecondary}
          mode="dropdown"
        >
          {languageOptions.map((lang) => (
            <Picker.Item
              key={lang.value}
              label={lang.label}
              value={lang.value}
              color={theme.colors.text}
            />
          ))}
        </Picker>

        {Platform.OS === 'web' && (
          <style>{`
            select {
              background-color: ${theme.colors.backgroundElevated} !important;
              color: ${theme.colors.text} !important;
            }
            select option {
              background-color: ${theme.colors.backgroundElevated};
              color: ${theme.colors.text};
            }
          `}</style>
        )}
      </View>

      {isChanging && (
        <View style={styles.changingIndicator}>
          <ActivityIndicator size="small" color={theme.colors.primary} />
          <Text style={styles.changingText}>{t('common.saving')}</Text>
        </View>
      )}
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    sectionContainer: {
      backgroundColor: theme.colors.surfaceSecondary,
      borderRadius: theme.radii.lg,
      padding: theme.spacing.lg,
      marginBottom: theme.spacing['2xl'],
      borderWidth: 2,
      borderColor: theme.colors.border,
    },
    sectionLabel: {
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightSemibold,
      color: theme.colors.text,
      marginBottom: theme.spacing.xs,
    },
    sectionDescription: {
      fontSize: theme.typography.fontSizeMd,
      color: theme.colors.textSecondary,
      marginBottom: theme.spacing.md,
    },
    pickerWrapper: {
      borderRadius: theme.radii.md,
      overflow: 'hidden',
      backgroundColor: theme.colors.backgroundElevated,
    },
    picker: {
      height: Platform.OS === 'web' ? 44 : undefined,
      fontSize: theme.typography.fontSizeMd,
    },
    changingIndicator: {
      flexDirection: 'row',
      alignItems: 'center',
      marginTop: theme.spacing.sm,
    },
    changingText: {
      marginLeft: theme.spacing.sm,
      fontSize: theme.typography.fontSizeMd,
      color: theme.colors.textSecondary,
    },
  });
}
